import React from 'react';
import { Link } from '@tanstack/react-router';
import { Button, Text } from '@chatsdet/ui';
import {
  ArrowSquareOut,
  DownloadSimple,
  FileText,
  Globe,
  Terminal,
  VideoCamera,
} from '@phosphor-icons/react';
import type { FailureDiagnostic } from '@chatsdet/types/issues';
import { API_URL } from '../../../config';
import { formatDurationMs } from '../../../lib/formatDuration';
import type { ExternalRunItemDto } from '../../../features/external-tests/useExternalRun';
import type { ExternalScenarioDetailDto } from '../../../features/external-tests/useExternalScenarioDetail';
import { WORKFLOW_HEALTH_META } from '../flow-list/components/cells';
import { DiagnosticHeadline } from '../FlowDesigner/runs/DiagnosticHeadline';
import { FailureDiagnosticSection } from '../FlowDesigner/runs/FailureDiagnosticSection';
import {
  displayLabel,
  externalHistoryPassRate,
  externalHistoryRunResult,
} from './externalRunHistory';

const CARD = 'rounded-lg border border-border bg-card';
const CARD_TITLE = 'border-b border-border px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground';

function Card({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className={CARD}>
      <div className={`flex items-center justify-between gap-2 ${CARD_TITLE}`}>
        <span>{title}</span>
        {aside}
      </div>
      {children}
    </section>
  );
}

function formatBytes(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const formatWhen = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

/** Spec-file path out of `<repo>/<spec>#<describe › … › leaf>`. */
function specFileOf(testKey: string): string | null {
  const [filePart = ''] = testKey.split('#');
  return filePart ? filePart.replace(/\/{2,}/g, '/').replace(/^\/+/, '') : null;
}

function ArtifactRow({
  icon,
  name,
  meta,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  name: string;
  meta?: string | null;
  actionLabel: string;
  onAction?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-2 last:border-b-0">
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <div className="min-w-0 flex-1">
        <Text size="sm" className="block truncate" title={name}>
          {name}
        </Text>
        {meta ? (
          <Text size="xs" className="block truncate text-muted-foreground">
            {meta}
          </Text>
        ) : null}
      </div>
      <Button variant="ghost" size="sm" onPress={onAction} isDisabled={!onAction}>
        {actionLabel}
      </Button>
    </div>
  );
}

const openArtifact = (openPath: string) =>
  window.open(`${API_URL}${openPath}`, '_blank', 'noopener,noreferrer');

function DetailRow({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline gap-3 px-4 py-1">
      <Text size="xs" className="w-20 shrink-0 text-muted-foreground">
        {label}
      </Text>
      <Text
        size="xs"
        className={`min-w-0 flex-1 break-words ${mono ? 'font-mono text-muted-foreground' : ''}`}
        title={value}
      >
        {value}
      </Text>
    </div>
  );
}

const RESULT_DOT: Record<ReturnType<typeof externalHistoryRunResult>, string> = {
  pass: 'bg-success',
  fail: 'bg-destructive',
  flaky: 'bg-amber-500',
  running: 'bg-primary',
  skip: 'bg-muted-foreground/40',
  idle: 'bg-muted',
};

/** The right column: failure analysis, evidence, run facts, and how this test has fared. */
export function ExternalTestRail({
  run,
  runId,
  selectedItem,
  diagnosticEnabled,
  diagnosticStatus,
  diagnostic,
  onTriggerDiagnostic,
  isTriggeringDiagnostic,
  onSeek,
  onViewAnalysis,
  hasRecording,
  onOpenRecording,
  onOpenDiagnostics,
  diagnosticCounts,
}: {
  run: ExternalScenarioDetailDto;
  runId: string;
  selectedItem: ExternalRunItemDto;
  diagnosticEnabled: boolean;
  diagnosticStatus: string | null;
  diagnostic: FailureDiagnostic | null;
  onTriggerDiagnostic: () => void;
  isTriggeringDiagnostic: boolean;
  onSeek: ((ms: number) => void) | undefined;
  onViewAnalysis: () => void;
  hasRecording: boolean;
  onOpenRecording: () => void;
  onOpenDiagnostics: () => void;
  /** Known only after the diagnostics have been loaded once. */
  diagnosticCounts: { console: number; network: number } | null;
}) {
  const location = selectedItem.location;
  const specFile = specFileOf(selectedItem.testKey);
  const history = run.history;
  const passRate = externalHistoryPassRate(history.runs);
  const health = WORKFLOW_HEALTH_META[history.health];
  const screenshots = selectedItem.artifacts.filter((a) => a.kind === 'screenshot');
  const traces = selectedItem.artifacts.filter((a) => a.kind === 'trace');
  const others = selectedItem.artifacts.filter((a) => a.kind === 'report' || a.kind === 'log');
  const recordingMeta = hasRecording
    ? selectedItem.capture?.videoDurationMs != null
      ? formatDurationMs(selectedItem.capture.videoDurationMs)
      : 'Available'
    : 'Not available';

  return (
    <div className="space-y-4">
      {diagnosticEnabled ? (
        <Card title="Failure Analysis">
          {diagnosticStatus === 'completed' && diagnostic ? (
            <div className="px-4 py-3">
              <DiagnosticHeadline
                diagnostic={diagnostic}
                onSelectEvent={onSeek}
                onViewAnalysis={onViewAnalysis}
              />
            </div>
          ) : (
            <div className="p-3">
              <FailureDiagnosticSection
                status={diagnosticStatus}
                diagnostic={diagnostic}
                onTrigger={onTriggerDiagnostic}
                isTriggering={isTriggeringDiagnostic}
              />
            </div>
          )}
        </Card>
      ) : null}

      <Card title="Artifacts">
        {screenshots.map((artifact) => {
          const failed = selectedItem.status === 'failed';
          const size = formatBytes(artifact.sizeBytes);
          return (
            <div key={artifact.id} className="border-b border-border p-3">
              <button
                type="button"
                onClick={() => openArtifact(artifact.openPath)}
                aria-label={failed ? 'Open failure screenshot' : 'Open final screenshot'}
                className="block w-full overflow-hidden rounded-md border border-border bg-card"
              >
                <img
                  src={`${API_URL}${artifact.openPath}`}
                  alt={failed ? 'Failure screenshot' : 'Final screenshot'}
                  loading="lazy"
                  className="max-h-48 w-full object-cover object-top"
                />
              </button>
              <Text size="xs" className="mt-2 block truncate text-muted-foreground">
                {failed ? 'Failure Screenshot' : 'Final Screenshot'}
                {size ? ` · ${size}` : ''}
              </Text>
            </div>
          );
        })}
        <ArtifactRow
          icon={<VideoCamera size={18} />}
          name="Recording"
          meta={recordingMeta}
          actionLabel={hasRecording ? 'Play' : 'Details'}
          onAction={onOpenRecording}
        />
        {traces.map((artifact) => (
          <ArtifactRow
            key={artifact.id}
            icon={<FileText size={18} />}
            name={artifact.name}
            meta={`Trace${formatBytes(artifact.sizeBytes) ? ` · ${formatBytes(artifact.sizeBytes)}` : ''}`}
            actionLabel="Download"
            onAction={() => openArtifact(artifact.openPath)}
          />
        ))}
        {others.map((artifact) => (
          <ArtifactRow
            key={artifact.id}
            icon={<DownloadSimple size={18} />}
            name={artifact.name}
            meta={`${artifact.kind === 'report' ? 'Report' : 'Log'}${formatBytes(artifact.sizeBytes) ? ` · ${formatBytes(artifact.sizeBytes)}` : ''}`}
            actionLabel="Download"
            onAction={() => openArtifact(artifact.openPath)}
          />
        ))}
        <ArtifactRow
          icon={<Globe size={18} />}
          name="Network log"
          meta={
            diagnosticCounts
              ? `${diagnosticCounts.network} requests`
              : hasRecording
                ? null
                : 'Needs a recording'
          }
          actionLabel="Open"
          onAction={hasRecording ? onOpenDiagnostics : undefined}
        />
        <ArtifactRow
          icon={<Terminal size={18} />}
          name="Console output"
          meta={
            diagnosticCounts
              ? `${diagnosticCounts.console} messages`
              : hasRecording
                ? null
                : 'Needs a recording'
          }
          actionLabel="Open"
          onAction={hasRecording ? onOpenDiagnostics : undefined}
        />
      </Card>

      <Card title="Run Details">
        <div className="py-2">
          {selectedItem.properties.map((property, i) => (
            <DetailRow key={`${property.label}-${i}`} label={property.label} value={property.value} />
          ))}
          <DetailRow
            label="Spec"
            value={location ? `${location.file}:${location.line}` : specFile}
            mono
          />
          <DetailRow label="Revision" value={run.revision} mono />
          <DetailRow label="Provider" value={run.provider} />
          <DetailRow label="Target" value={run.targetUrl} />
          <DetailRow label="Started" value={formatWhen(selectedItem.startedAt)} />
          <DetailRow label="Ended" value={formatWhen(selectedItem.finishedAt)} />
          <DetailRow label="Run id" value={runId} mono />
          <DetailRow label="Test id" value={selectedItem.id} mono />
        </div>
      </Card>

      {history.runs.length > 0 ? (
        <Card
          title="Recent History"
          aside={
            <span className="flex items-center gap-1.5 normal-case tracking-normal">
              <health.Icon size={14} weight="fill" style={{ color: health.color }} aria-label={`Health: ${health.label}`} />
              {passRate !== null ? (
                <span className={`font-mono tabular-nums ${passRate < 50 ? 'text-destructive' : ''}`}>
                  {passRate}%
                </span>
              ) : null}
            </span>
          }
        >
          <ul>
            {history.runs.slice(0, 10).map((entry) => {
              const isCurrent = entry.runId === runId;
              return (
                <li key={entry.runId} className="border-b border-border last:border-b-0">
                  <Link
                    to="/runs/external/$runId/item/$itemId"
                    params={{ runId: entry.runId, itemId: entry.itemId }}
                    className={`flex items-center gap-3 px-4 py-2 transition-colors hover:bg-muted/40 ${isCurrent ? 'bg-muted/30' : ''}`}
                  >
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${RESULT_DOT[externalHistoryRunResult(entry)]}`}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <Text size="xs" className="block truncate">
                        {run.runnerName ?? 'External run'}
                      </Text>
                      <Text size="xs" className="block text-muted-foreground">
                        {formatWhen(entry.createdAt)} · {displayLabel(entry)}
                      </Text>
                    </div>
                    {isCurrent ? (
                      <Text size="xs" weight="medium">
                        This run
                      </Text>
                    ) : (
                      <ArrowSquareOut size={14} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
